import Heading from '@/components/heading/Heading';
import Padding from '@/components/padding/Padding';
import { Box, Breadcrumb, Grid, Heading as ChakraHeading, Text } from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import Footer from '../home/components/Footer';
import ArticleCard from './components/ArticleCard';
import SearchBox from './components/SearchBox';
import SearchResults from './components/SearchResults';
import { ARTICLE_METADATA, ARTICLE_SLUGS } from './constants/articleMetadata';
import useLearnMoreSearch from './hooks/useLearnMoreSearch';

type Grecaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

const LearnMoreLanding = (): React.JSX.Element => {
  const { query, setQuery, search, isLoading, response, error } = useLearnMoreSearch();

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

  const loadRecaptcha = useCallback(async (): Promise<Grecaptcha> => {
    if (typeof window === 'undefined') {
      throw new Error('Window is not available');
    }

    const w = window as unknown as { grecaptcha?: Grecaptcha };

    // If already available, resolve when it's ready
    if (w.grecaptcha && typeof w.grecaptcha.execute === 'function') {
      await new Promise<void>((resolve) => w.grecaptcha!.ready(() => resolve()));
      return w.grecaptcha!;
    }

    if (!siteKey) {
      throw new Error('Missing VITE_RECAPTCHA_SITE_KEY');
    }

    // Inject script if not present
    let script = document.getElementById('recaptcha-v3') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'recaptcha-v3';
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    await new Promise<void>((resolve, reject) => {
      const onLoad = () => {
        const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
        if (!g) {
          reject(new Error('grecaptcha unavailable after script load'));
          return;
        }
        g.ready(() => resolve());
      };
      script!.addEventListener('load', onLoad, { once: true });
      script!.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script')), {
        once: true,
      });
      // If it was already loaded before listeners attached
      const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
      if (g?.ready) {
        g.ready(() => resolve());
      }
    });

    return (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha as Grecaptcha;
  }, [siteKey]);

  useEffect(() => {
    // Preload recaptcha in the background
    loadRecaptcha().catch(() => {
      // silently ignore here; will show error on submit if needed
    });
  }, [loadRecaptcha]);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      return;
    }

    try {
      const grecaptcha = await loadRecaptcha();
      const token = await grecaptcha.execute(siteKey!, { action: 'learn_more_search' });
      await search(token);
    } catch (err) {
      console.error('Search error:', err);
    }
  }, [query, loadRecaptcha, siteKey, search]);

  return (
    <Box>
      <Heading />
      <Padding verticalPadding={12}>
        <Breadcrumb.Root size="md" marginBottom={{ base: 6, lg: 8 }}>
          <Breadcrumb.List>
            <Breadcrumb.Item>
              <Breadcrumb.Link href="/">Home</Breadcrumb.Link>
            </Breadcrumb.Item>
            <Breadcrumb.Separator />
            <Breadcrumb.Item>
              <Breadcrumb.CurrentLink>Learn More</Breadcrumb.CurrentLink>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb.Root>

        {/* Search Section */}
        <Box textAlign="center" py={{ base: 8, md: 12 }}>
          <ChakraHeading
            as="h1"
            size={{ base: '2xl', md: '3xl' }}
            mb={6}
            style={{ fontFamily: 'Bebas Neue' }}
          >
            How can we help you?
          </ChakraHeading>
          <SearchBox
            value={query}
            onChange={setQuery}
            onSearch={handleSearch}
            isLoading={isLoading}
            placeholder="Ask a question about Olympiq..."
          />
        </Box>

        {/* Search Results */}
        {error && (
          <Box
            bg="red.50"
            _dark={{ bg: 'red.900' }}
            borderRadius="xl"
            p={4}
            mt={8}
            maxW="800px"
            mx="auto"
          >
            <Text color="red.600" _dark={{ color: 'red.300' }}>
              {error}. Please try again.
            </Text>
          </Box>
        )}

        {response && <SearchResults response={response} />}

        {/* Article Grid */}
        <Box mt={{ base: 10, md: 14 }}>
          <ChakraHeading
            as="h2"
            size="lg"
            mb={6}
            textAlign="center"
            color="gray.700"
            _dark={{ color: 'gray.300' }}
          >
            Browse All Articles
          </ChakraHeading>
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            }}
            gap={6}
          >
            {ARTICLE_SLUGS.map((slug) => {
              const { title, description, Icon, gradient } = ARTICLE_METADATA[slug];
              return (
                <ArticleCard
                  key={slug}
                  slug={slug}
                  title={title}
                  description={description}
                  Icon={Icon}
                  gradient={gradient}
                />
              );
            })}
          </Grid>
        </Box>
      </Padding>
      <Footer />
    </Box>
  );
};

export default LearnMoreLanding;
